import { useEffect, useRef, useState } from "react"

import { ArrowLeft, LoaderCircle } from "lucide-react"

import BackIcon from "@/assets/img/soulmate/camera/back.svg"
import CameraOffIcon from "@/assets/img/soulmate/camera/camera-off.svg"
import CheckIcon from "@/assets/img/soulmate/camera/check.svg"
import RefreshIcon from "@/assets/img/soulmate/camera/refresh.svg"
import SnapIcon from "@/assets/img/soulmate/camera/snap.svg"
import HeroFemale from "@/assets/img/soulmate/selfie/hero-female.png"
import HeroMale from "@/assets/img/soulmate/selfie/hero-male.png"
import PrivacyShield from "@/assets/img/soulmate/selfie/privacy-shield.png"
import { Button } from "@/components/ui/button"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { cn } from "@/lib/utils"
import { CameraFlash, CameraMask, useLiveFaceMesh } from "@/packages/camera"
import { StaticFaceMesh, prepareUploadedPhoto, useFaceLandmarker } from "@/packages/face-mesh"
import { useMediaStore } from "@/stores/media-store"
import { PHOTO_CAPTURED } from "./photo-step"
import type { StepComponentProps } from "./step-contract"

/**
 * Sub-screens of the selfie flow, all phases of this one component: the intro
 * (our Soulmate Figma screen), the two camera error states, and the capture
 * screens (camera loading → live view → captured-photo preview).
 */
type Phase = "intro" | "access" | "not-detected" | "loading" | "camera" | "preview" | "analyzing"

const TROUBLESHOOTING = [
  "Allow camera access in your browser settings",
  "Close other apps that may be using the camera",
  "Refresh the page or try a different browser/device",
]

/**
 * Soulmate selfie flow. The intro (hero + privacy + CTAs) is our Soulmate design;
 * the actual capture screens — full-screen camera with the oval mask, the live
 * face mesh, the "Camera is starting…" loader, and the photo preview — are the
 * origene CameraScreenV2 look, ported 1:1 onto our stack.
 *
 * "Take a photo" opens the front camera via `getUserMedia({ facingMode: "user" })`.
 * The shutter grabs a mirrored frame into the media store (keyed by this step's
 * id) and snapshots the live face-mesh detection alongside it, so the processing
 * screen can replay the mesh. "Upload from gallery" feeds the same preview from a
 * file (no detection). Error screens explain the problem but never block the funnel.
 */
export function SoulmateSelfieStep({ step, onChange, onAdvance, onBack, answers }: StepComponentProps) {
  const [phase, setPhase] = useState<Phase>("intro")
  const captured = useMediaStore((s) => s.photos[step.id])
  const capturedMesh = useMediaStore((s) => s.meshes[step.id])
  const setPhoto = useMediaStore((s) => s.setPhoto)
  const setMesh = useMediaStore((s) => s.setMesh)
  const clearPhoto = useMediaStore((s) => s.clearPhoto)
  const [videoReady, setVideoReady] = useState(false)
  const [flash, setFlash] = useState(false)

  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Preload the face-mesh model so it's warm by the time the camera opens; the
  // live overlay loop runs only while the camera shows frames, and exposes the
  // latest detection for the shutter to snapshot.
  const { landmarker } = useFaceLandmarker()
  const { canvasRef, lastCaptureRef } = useLiveFaceMesh(
    videoRef,
    landmarker,
    phase === "camera" && videoReady,
  )

  // Hero follows the user's own gender; female is the default (also "other").
  const hero = answers.gender === "male" ? HeroMale : HeroFemale

  function stopCamera() {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }

  useEffect(() => stopCamera, [])

  // Do NOT warm the still-image (CPU) detector here. origene runs a SINGLE
  // FaceLandmarker graph; creating the CPU image graph in parallel with the live
  // GPU video graph clashes on mobile and the live mesh then silently never
  // detects. The image detector is loaded lazily on an actual gallery upload
  // instead (see prepareUploadedPhoto), so the camera path keeps a single graph.

  // Attach the live stream once the <video> for the camera phase is mounted.
  useEffect(() => {
    if (phase !== "camera") return
    const video = videoRef.current
    if (!video || !streamRef.current) return
    video.srcObject = streamRef.current
    video.play().catch(() => {})
  }, [phase])

  async function startCamera() {
    clearPhoto(step.id)
    onChange("")
    setVideoReady(false)
    setPhase("loading")
    const media = navigator.mediaDevices
    if (!media?.getUserMedia) {
      setPhase("not-detected")
      return
    }
    try {
      // Request a high-res feed (like origene). The mesh canvas is backed at the
      // video's pixel size, so a bigger capture downscales to the screen — making
      // the same-width connectors render thin and crisp instead of chunky. `ideal`
      // degrades gracefully when the camera can't hit 1440p.
      const stream = await media.getUserMedia({
        video: { facingMode: "user", width: { ideal: 2560 }, height: { ideal: 1440 } },
      })
      streamRef.current = stream
      setPhase("camera")
    } catch (err) {
      const name = err instanceof DOMException ? err.name : ""
      setPhase(name === "NotAllowedError" ? "access" : "not-detected")
    }
  }

  function store(dataUrl: string) {
    setPhoto(step.id, dataUrl)
    onChange(PHOTO_CAPTURED)
  }

  function capturePhoto() {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    const canvas = document.createElement("canvas")
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    // Mirror to match the selfie-style preview the user just saw.
    ctx.translate(canvas.width, 0)
    ctx.scale(-1, 1)
    ctx.drawImage(video, 0, 0)
    store(canvas.toDataURL("image/jpeg", 0.92))
    // Detect once more on the captured frame (origene usePhotoCapture) so the
    // processing screen always gets a mesh, even if the live loop missed the very
    // last frame; fall back to the last live detection on error. performance.now()
    // keeps climbing, so re-running detectForVideo here keeps timestamps monotonic.
    try {
      const results = landmarker?.detectForVideo(video, performance.now())
      setMesh(
        step.id,
        results
          ? { results, videoWidth: video.videoWidth, videoHeight: video.videoHeight, mirrored: true }
          : lastCaptureRef.current,
      )
    } catch {
      setMesh(step.id, lastCaptureRef.current)
    }
    setFlash(true)
    stopCamera()
    setPhase("preview")
  }

  function openFilePicker() {
    fileInputRef.current?.click()
  }

  function handleFilePicked(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file) return
    // Show the "Analyzing…" loader while we orient, re-encode and face-detect the
    // upload, then land on the preview with the photo AND mesh together — no gap
    // where the photo shows without its mesh. This is the gallery counterpart of
    // the camera's live capture.
    clearPhoto(step.id)
    onChange("")
    setPhase("analyzing")
    prepareUploadedPhoto(file)
      .then((prepared) => {
        if (!prepared) {
          setPhase("intro") // couldn't decode the file — back to the start
          return
        }
        store(prepared.dataUrl)
        setMesh(step.id, prepared.mesh) // null when no face → preview shows no overlay
        setPhase("preview")
      })
      .catch(() => setPhase("intro"))
  }

  const handleBack = () => {
    if (phase === "intro") {
      onBack?.()
    } else {
      stopCamera()
      setPhase("intro")
    }
  }

  // ── Intro (our Soulmate Figma design) ───────────────────────────────────────
  if (phase === "intro") {
    return (
      <div className="flex flex-1 flex-col bg-dark-base text-purple-light">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFilePicked}
        />
        <div className="relative flex-1 overflow-hidden">
          <img src={hero} alt="" className="absolute inset-0 size-full object-cover" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-transparent to-dark-base" />
          <div className="absolute inset-x-0 top-0 flex h-14 items-center px-4">
            <button
              type="button"
              onClick={handleBack}
              aria-label="Go back"
              className="-ml-2 flex size-10 items-center justify-center rounded-full text-purple-light transition-colors hover:bg-purple-light/10"
            >
              <ArrowLeft className="size-6" />
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-6 px-4 pt-3 pb-safe">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2 text-center">
              <h1 className="font-display text-2xl font-medium text-purple-light">{step.title}</h1>
              {step.subtitle && <p className="text-base text-purple-light/70">{step.subtitle}</p>}
            </div>

            <div className="flex flex-col items-center gap-1 rounded-[20px] bg-dark-surface px-4 pt-3 pb-4 text-center">
              <img src={PrivacyShield} alt="" className="size-10" />
              <p className="text-base leading-5 font-semibold text-white">100% Private Processing</p>
              <p className="text-sm text-purple-light/70">
                Your photo is processed securely, never saved, and never shared with anyone.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              variant="gradient"
              size="xl"
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.SOULMATE_QUIZ_PHOTO, {
                  metadata: { source: "take_photo" },
                })
                startCamera()
              }}
            >
              Take a photo
            </Button>
            <Button
              variant="ghost"
              size="xl"
              className="bg-purple-light/10 text-purple-light hover:bg-purple-light/15 hover:text-purple-light"
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.SOULMATE_QUIZ_PHOTO, {
                  metadata: { source: "gallery" },
                })
                openFilePicker()
              }}
            >
              Upload from gallery
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // ── Camera error states: origene bottom sheet over a black screen ────────────
  if (phase === "access" || phase === "not-detected") {
    const isNotDetected = phase === "not-detected"
    return (
      <div className="fixed inset-y-0 left-1/2 z-50 flex w-full max-w-app -translate-x-1/2 flex-col overflow-hidden bg-black/95">
        <div className="flex h-14 items-center px-4">
          <button
            type="button"
            onClick={handleBack}
            aria-label="Go back"
            className="-ml-2 flex size-10 items-center justify-center rounded-full transition-opacity hover:opacity-70"
          >
            <img src={BackIcon} alt="" className="size-9" />
          </button>
        </div>

        <div className="mt-auto flex w-full flex-col items-center gap-4 rounded-t-[24px] bg-dark-surface px-5 pt-6 pb-safe text-center">
          <span className="flex size-24 items-center justify-center rounded-full bg-destructive/10">
            <img src={CameraOffIcon} alt="" className="size-10" />
          </span>

          <div className="flex flex-col gap-1">
            <h1 className="font-display text-2xl font-medium text-purple-light">
              {isNotDetected ? "Camera not detected" : "Camera access needed"}
            </h1>
            <p className="text-base text-purple-light/70">
              {isNotDetected
                ? "We need your camera to scan your face and reveal your soulmate reading. Without it, key facial traits can't be scanned."
                : "Your browser has blocked camera permissions. We need access to analyze your unique features and create a sketch of your perfect match."}
            </p>
          </div>

          {isNotDetected && (
            <div className="flex w-full flex-col gap-1 rounded-2xl bg-purple-light/10 p-4 text-left">
              <p className="text-base leading-5 font-semibold text-white">What you can do:</p>
              <ul className="flex flex-col gap-0.5 text-sm text-purple-light/70">
                {TROUBLESHOOTING.map((tip) => (
                  <li key={tip} className="flex gap-1.5">
                    <span aria-hidden>•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button variant="gradient" size="xl" onClick={startCamera}>
            Got it
          </Button>
        </div>
      </div>
    )
  }

  // ── Analyzing an uploaded photo (orient + re-encode + face-detect) ───────────
  if (phase === "analyzing") {
    return (
      <div className="fixed inset-y-0 left-1/2 z-50 flex w-full max-w-app -translate-x-1/2 flex-col items-center justify-center gap-2.5 overflow-hidden bg-black">
        <LoaderCircle className="size-6 animate-spin text-white" />
        <span className="font-body text-xl text-white">Analyzing your photo…</span>
      </div>
    )
  }

  // ── Capture: full-screen camera (origene look) ──────────────────────────────
  const showStarting = !captured && !videoReady // "Camera is starting…" until first frame
  return (
    <div className="fixed inset-y-0 left-1/2 z-50 flex w-full max-w-app -translate-x-1/2 items-center justify-center overflow-hidden bg-black">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFilePicked}
      />

      {/* Camera-is-starting loader */}
      {showStarting && (
        <div className="absolute top-1/2 left-1/2 z-[100] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2.5">
          <LoaderCircle className="size-6 animate-spin text-white" />
          <span className="font-body text-xl text-white">Camera is starting…</span>
        </div>
      )}

      {/* Camera UI — video/photo, mask, header and controls all fade in together once
          there's a frame or a photo. Like origene's single opacity gate: while the
          camera boots, only the "Camera is starting…" loader above is visible. */}
      <div
        className="absolute inset-0 transition-opacity duration-300"
        style={{ opacity: captured || videoReady ? 1 : 0 }}
      >
        {captured ? (
          <>
            <img src={captured} alt="" className="size-full object-cover" />
            <StaticFaceMesh
              capture={capturedMesh ?? null}
              full
              className="pointer-events-none absolute inset-0 size-full object-cover"
            />
          </>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              onPlaying={() => setTimeout(() => setVideoReady(true), 450)}
              className="size-full -scale-x-100 object-cover"
            />
            {/* Live face mesh, mirrored in-draw to match the -scale-x-100 video. */}
            <canvas
              ref={canvasRef}
              className="pointer-events-none absolute inset-0 size-full object-cover"
            />
          </>
        )}

        <CameraMask />

        {/* Header: centered copy on both camera and preview; the back arrow only on
            the live camera, not the captured preview (origene FinishedPhotoWindow). */}
        <div className="absolute inset-x-0 top-0 z-20 flex items-start px-5 pt-[max(0.75rem,env(safe-area-inset-top))]">
          {!captured && (
            <button
              type="button"
              onClick={handleBack}
              aria-label="Go back"
              className="absolute left-4 top-2 transition-opacity hover:opacity-70"
            >
              <img src={BackIcon} alt="" className="size-9" />
            </button>
          )}
          <div className="mx-auto flex flex-col items-center gap-0.5 text-center">
            <p className="font-body text-sm font-medium text-white">Let&rsquo;s capture your face</p>
            <p className="font-body text-xs text-white/60">Stay still, centered, and in good light</p>
          </div>
        </div>

        {/* Controls: shutter (live) or Retake / Looks good (preview) */}
        {captured ? (
          <div className="absolute inset-x-0 bottom-[max(1.25rem,env(safe-area-inset-bottom))] z-20 flex w-full justify-center gap-6 px-4">
            <button
              type="button"
              onClick={startCamera}
              className="flex h-[52px] flex-1 items-center justify-center gap-2 rounded-full bg-white/15 font-body text-base font-semibold text-white backdrop-blur transition-colors hover:bg-white/20"
            >
              <img src={RefreshIcon} alt="" className="size-6" />
              Retake
            </button>
            <button
              type="button"
              onClick={onAdvance}
              className="flex h-[52px] flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white transition-opacity hover:opacity-90"
            >
              <img src={CheckIcon} alt="" className="size-6" />
              Looks good
            </button>
          </div>
        ) : (
          <div className="absolute inset-x-0 bottom-[max(1.25rem,env(safe-area-inset-bottom))] z-20 flex justify-center">
            <button
              type="button"
              onClick={capturePhoto}
              disabled={!videoReady}
              aria-label="Take photo"
              className={cn("transition-opacity", !videoReady && "opacity-50")}
            >
              <img src={SnapIcon} alt="" className="w-[78px]" />
            </button>
          </div>
        )}
      </div>

      {flash && <CameraFlash onComplete={() => setFlash(false)} />}
    </div>
  )
}
